'use client';

import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ChatMessage, TaskFile } from '@/entities/interview-task';
import {
	createViewTracker,
	useCandidateAnalytics,
} from '@/features/candidate-interview';
import type { CandidateInterviewItemReplayV1 } from '@/shared/api';

import { mapReplayMessages } from '../lib/replay-messages';
import { ChatPane } from './chat-pane';
import { type OpenedQuiz, SidePanel } from './side-panel';

const noop = () => {};

/** The ordinary chat presentation, backed only by this attempt's saved dialog. */
export function CandidateReplay({
	replay,
}: {
	replay: CandidateInterviewItemReplayV1;
}) {
	const track = useCandidateAnalytics(replay.interviewId, 'replay');
	const seen = useRef(createViewTracker());
	const messages = useMemo(
		() => mapReplayMessages(replay.messages),
		[replay.messages],
	);
	const [openedFile, setOpenedFile] = useState<TaskFile | null>(null);
	const [openedQuiz, setOpenedQuiz] = useState<OpenedQuiz | null>(null);
	const sidePanelVisible = !!openedFile || !!openedQuiz;
	const title = replay.problem?.title || `Задача ${replay.item.position + 1}`;
	const resultHref = `/interviews/${encodeURIComponent(replay.interviewId)}?attempt=${encodeURIComponent(replay.attemptId)}`;
	useEffect(() => {
		if (seen.current(replay.attemptId))
			track('replay_view', { position: replay.item.position });
	}, [replay.attemptId, replay.item.position, track]);
	const closePanel = () => {
		if (sidePanelVisible)
			track('replay_file_close', { position: replay.item.position });
		setOpenedFile(null);
		setOpenedQuiz(null);
	};
	const openFile = (file: TaskFile) => {
		if (openedFile === file) return;
		if (sidePanelVisible)
			track('replay_file_close', { position: replay.item.position });
		setOpenedFile(file);
		setOpenedQuiz(null);
		track('replay_file_open', { position: replay.item.position });
	};
	const openQuiz = (message: ChatMessage) => {
		if (!message.quiz) return;
		const key = String(message.messageId);
		if (openedQuiz?.key === key) return;
		if (sidePanelVisible)
			track('replay_file_close', { position: replay.item.position });
		setOpenedFile(null);
		setOpenedQuiz({ key, question: message.quiz.question });
		track('replay_file_open', { position: replay.item.position });
	};
	const returnLink = (
		<Link
			href={resultHref}
			onClick={() =>
				track('back_to_result', { position: replay.item.position })
			}
			className="inline-flex items-center gap-2 rounded-lg text-sm text-gray-3 hover:text-black focus-visible:ring-2 focus-visible:ring-green"
		>
			<ArrowLeft size={16} aria-hidden="true" />К результату собеседования
		</Link>
	);
	if (!replay.dialog || messages.length === 0)
		return (
			<main className="h-full overflow-auto bg-gray-5 px-6 py-8">
				<div className="mx-auto max-w-[720px]">
					{returnLink}
					<h1 className="my-5 text-2xl font-semibold">{title}</h1>
					<p className="text-gray-3">
						{!replay.dialog
							? 'Эта задача не была начата. Сохранённого диалога нет.'
							: 'В сохранённом диалоге нет сообщений.'}
					</p>
				</div>
			</main>
		);
	return (
		<main className="relative flex h-full min-h-0 min-w-0 flex-1 bg-gray-5">
			<div
				className={`flex min-w-0 flex-1 flex-col ${sidePanelVisible ? 'md:flex-[0_0_56%]' : ''}`}
			>
				<ChatPane
					navigation={{ backHref: resultHref, backLabel: 'К результату' }}
					title={title}
					readOnly
					readOnlyFooter={
						<div className="flex flex-wrap items-center justify-between gap-3 py-2">
							<span className="text-sm text-gray-3">
								Сохранённый диалог · Только просмотр
							</span>
							{returnLink}
						</div>
					}
					messages={messages}
					initialMessages={replay.messages}
					breadcrumbs={[{ label: 'Собеседование' }, { label: title }]}
					pinnedTitle={title}
					pinnedBarShown
					latestFileMsgIdx={messages.findIndex(
						(message) =>
							message.role === 'interviewer' && !!message.attaches?.length,
					)}
					onOpenFile={openFile}
					onOpenQuiz={openQuiz}
					getAttachStatus={() => null}
					getQuizStatus={() => null}
					isInterview={false}
					isThinking={false}
					hasCompletedBanner={false}
					hasStoppedBanner={false}
					canSendMessage={false}
					isCompaction={false}
					isReportPending={false}
					isInterviewStarted
					timerMm=""
					timerSs=""
					timerLow={false}
					onRate={noop}
					draft=""
					onDraftChange={noop}
					onSend={noop}
					onResetDialog={noop}
					onNext={noop}
					nextLabel=""
					onCoursePrev={noop}
					onCourseNext={noop}
					onLogout={noop}
					logoutDisabled
					onStartInterview={noop}
				/>
			</div>
			{sidePanelVisible && (
				<div className="absolute inset-0 z-20 flex min-w-0 md:static md:flex-1">
					<SidePanel
						openedFile={openedFile}
						openedQuiz={openedQuiz}
						pinnedFiles={
							openedFile
								? (messages.find((message) =>
										message.attaches?.some((file) => file === openedFile),
									)?.attaches ?? [])
								: []
						}
						onSelectFile={openFile}
						onClose={closePanel}
						getEditorDraft={() => ''}
						setEditorDraft={noop}
						footer={null}
						quizIsLocked
						actionsDisabled
						onQuizAttempt={noop}
						onQuizCorrect={noop}
						onQuizWrong={noop}
					/>
				</div>
			)}
		</main>
	);
}
